import { useEffect } from 'react';
import { useAppData } from '@/contexts/AppDataContext';
import { extractAppleAppId } from '@/lib/appStoreLinks';

export const DynamicFavicon = () => {
  const { config } = useAppData();

  useEffect(() => {
    if (config && config.icon?.light) {
      // Pre-load the favicon image to ensure it's ready
      const img = new Image();
      img.onload = () => {
        // Remove existing dynamic favicon links (but keep the ones with IDs from index.html)
        const existingDynamicFavicons = document.querySelectorAll('link[rel*="icon"]:not([id^="favicon-"])');
        existingDynamicFavicons.forEach(favicon => {
          // Only remove if it's not our protected local favicons
          if (!favicon.id || (!favicon.id.startsWith('favicon-') && favicon.id !== 'dynamic-favicon')) {
            favicon.remove();
          }
        });

        // Remove or update the protected favicons
        const svgFavicon = document.getElementById('favicon-svg');
        const icoFavicon = document.getElementById('favicon-ico');
        
        if (svgFavicon) svgFavicon.remove();
        if (icoFavicon) icoFavicon.remove();

        // Create new dynamic favicon link
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.type = 'image/jpeg';
        favicon.href = config.icon?.light;
        favicon.id = 'dynamic-favicon';
        document.head.appendChild(favicon);

        // Also update apple-touch-icon for mobile devices
        const appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.href = config.icon?.light;
        appleTouchIcon.id = 'dynamic-apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      };
      img.src = config.icon?.light;
    }
    
    if (config) {
      // Do not set document.title / og:* here — SEOHead on each page owns those.
      // This effect runs after child SEOHead and was overwriting vehicle/home meta.

      const updateMetaName = (name: string, content: string) => {
        let metaTag = document.querySelector(`meta[name="${name}"]`);
        if (metaTag) {
          metaTag.setAttribute('content', content);
        } else {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('name', name);
          metaTag.setAttribute('content', content);
          document.head.appendChild(metaTag);
        }
      };

      const appleAppId = extractAppleAppId(config.appStore);

      // Apple iTunes App meta tag
      if (appleAppId) {
        updateMetaName('apple-itunes-app', `app-id=${appleAppId}`);
      }

      // Dynamic Web App Manifest
      if (config.appStore || config.googlePlay) {
        const manifest = {
          name: config.name || "App da Loja",
          short_name: config.name || "App da Loja",
          description: config.about || "App da Loja",
          prefer_related_applications: true,
          related_applications: [],
          display: "standalone"
        };

        // Add Google Play app if available
        if (config.googlePlay?.id) {
          manifest.related_applications.push({
            platform: "play",
            id: config.googlePlay.packageName
          });
        }

        // Add Apple App Store app if available
        if (appleAppId) {
          manifest.related_applications.push({
            platform: "itunes",
            url: `https://apps.apple.com/br/app/id${appleAppId}`
          });
        }

        // Remove existing manifest link
        const existingManifest = document.querySelector('link[rel="manifest"]');
        if (existingManifest) {
          existingManifest.remove();
        }

        // Create new manifest link
        const manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        manifestLink.href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
        document.head.appendChild(manifestLink);
      }
    }
  }, [config]);

  return null; // This component doesn't render anything
};
